import { until } from "@vueuse/core";

import type { MaybeRefOrGetter } from "vue";
import { toValue } from "vue";

import { useOrganizationManager } from "@/composables/organization-manager";
import { useStekManager } from "@/composables/stek-manager";
import { useAuthenticationStore } from "@/stores/authentication-store";

import { createClient as createFetchClient } from "./gen/client/client.gen";
import { type Client } from "./gen/client/types.gen";

const DEFAULT_BACKEND_URI = import.meta.env.VITE_API_URL;

export interface ServiceOpts {
    requires: {
        auth: boolean;
        organization: boolean;
        stek: boolean;
    };
    overrides?: {
        organizationId?: string;
        stekId?: string;
    };
}

interface ClientOpts extends ServiceOpts {
    backendURI?: MaybeRefOrGetter<string>;
}

export function createClient(opts?: ClientOpts): Client {
    return createFetchClient({
        baseUrl: toValue(opts?.backendURI || DEFAULT_BACKEND_URI),
        credentials: "omit",
    });
}

export const useClient = (opts: ClientOpts) => {
    const client = createClient(opts);
    client.interceptors.request.use(async (request) => {
        // It's important these are called inside the interceptor to work.
        const { getToken } = useAuthenticationStore();
        const { organization } = useOrganizationManager();
        const { stek } = useStekManager();

        if (opts.requires.auth) {
            const token = await getToken();
            if (!token) {
                throw Error("Request requires auth, but token not available.");
            }
            request.headers.set("Authorization", `Bearer ${token}`);
        }

        if (opts.requires.organization) {
            let organization_id = opts.overrides?.organizationId;
            if (!organization_id) {
                await until(organization).not.toBeUndefined({ timeout: 5000 });
                organization_id = organization.value?.identity;
            }
            if (!organization_id) {
                throw Error(
                    "Request requires organization, but organization not available."
                );
            }
            request.headers.set("Organization", organization_id);
        }

        if (opts.requires.stek) {
            let stek_id = opts.overrides?.stekId;
            if (!stek_id) {
                await until(stek).not.toBeUndefined({ timeout: 5000 });
                stek_id = stek.value?.identity;
            }
            if (!stek_id) {
                throw Error("Request requires stek, but stek not available.");
            }
            request.headers.set("Stek", stek_id);
        }

        return request;
    });

    // Add response interceptor to handle authentication and authorization errors
    client.interceptors.response.use(async (response) => {
        // Handle 401 and 403 errors globally
        if (response.status === 403) {
            // Clone the response before reading the body to avoid consuming it
            const clonedResponse = response.clone();
            const errorData = await clonedResponse.json().catch(() => null);

            // Set a global unauthorized state
            window.dispatchEvent(
                new CustomEvent("unauthorized-access", {
                    detail: {
                        status: response.status,
                        message:
                            errorData?.detail?.[0]?.message ||
                            "You don't have permission to access this resource",
                    },
                })
            );
        }
        return response;
    });

    return client;
};
